// src/api/index.js
import axios from "axios";
import i18n from "../i18n";

//
// 1) Read your env vars once
//
const API_URL = process.env.REACT_APP_API_URL;
const AUTH_KEY = process.env.REACT_APP_AUTH_TOKEN;

// sanity‐check
if (!API_URL) {
  console.warn(
    "⚠️ REACT_APP_API_URL is undefined! Did you restart after editing .env?"
  );
}
if (!AUTH_KEY) {
  console.warn("⚠️ REACT_APP_AUTH_KEY is undefined! Check your .env setup.");
}

//
// 2) Create one shared Axios client
//
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "X-API-KEY": AUTH_KEY,
    Accept: "application/json",
  },
});

//
// 3) Global error interceptor
//
apiClient.interceptors.response.use(
  (resp) => resp,
  (err) => {
    // You can customize message based on status
    const status = err.response?.status;
    let message = err.message;
    if (status === 401) message = "Unauthorized. Please log in.";
    else if (status === 403) message = "Forbidden. Check your permissions.";
    else if (err.code === "ERR_NETWORK")
      message = "Cannot reach server. Check network / CORS.";
    else if (err.response?.data?.message) message = err.response.data.message;

    console.error(`API Error [${status || err.code}]:`, message);
    return Promise.reject(new Error(message));
  }
);

//
// 4) Centralize all endpoint paths
//
const endpoints = {
  bundles: (lang) => `/api/bundles?lang=${lang}`,
  meals: (lang) => `/api/meals?lang=${lang}`,
  cities: (lang) => `/api/cities?lang=${lang}`,
  discounts: "/api/discounts",
  coupon: "/api/validate-coupon",
  cart: "/api/cart",
  products: (lang) => `/api/products?lang=${lang}`,
};

//
// 5) Expose easy helper functions
//
async function getBundles() {
  const { data } = await apiClient.get(endpoints.bundles(i18n.language));
  // you can validate that `data` is an array here, if you like
  return data;
}

async function getMeals() {
  const { data } = await apiClient.get(endpoints.meals(i18n.language));
  // you can validate that `data` is an array here, if you like
  return data;
}
async function getCities() {
  const { data } = await apiClient.get(endpoints.cities(i18n.language));
  // you can validate that `data` is an array here, if you like
  return data;
}
async function postCart(orderData) {
  const response = await apiClient.post(endpoints.cart, orderData);
  // return both status and payload
  return {
    status: response.status,
    data: response.data,
  };
}
async function isValidCoupon(coupon) {
  const response = await apiClient.post(endpoints.coupon, coupon);
  return {
    status: response.status,
    data: response.data,
  };
}
async function getDiscounts() {
  const { data } = await apiClient.get(endpoints.discounts);
  return data;
}
async function getProducts() {
  const { data } = await apiClient.get(endpoints.products(i18n.language));
  return data;
}

export const api = {
  getBundles,
  getMeals,
  getCities,
  postCart,
  isValidCoupon,
  getDiscounts,
  getProducts,
};
